// API service for connecting guest app with backend
const API_BASE_URL = 'http://localhost:3000/api';

export interface ReservationRequest {
  locationId: string;
  locationName: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  date: string;
  time: string;
  guests: number;
  specialRequests?: string;
}

export interface TakeawayOrder {
  locationId: string;
  locationName: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
  }>;
  totalAmount: number;
  pickupTime: string;
  specialRequests?: string;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  postcode: string;
  phone: string;
  coordinates: [number, number];
  isOpen: boolean;
  closingTime: string;
  services: string[];
  type: 'restaurant' | 'drive';
}

class ApiService {
  // Get all locations from backend
  async getLocations(): Promise<Location[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/locations`);
      if (!response.ok) {
        throw new Error('Failed to fetch locations');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Return sample data as fallback
      return this.getSampleLocations();
    }
  }

  // Submit a reservation to backend
  async submitReservation(reservation: ReservationRequest): Promise<{ success: boolean; reservationId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reservation),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit reservation');
      }

      const result = await response.json();
      return { success: true, reservationId: result.reservationId };
    } catch (error) {
      console.error('Error submitting reservation:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Submit a takeaway order to backend
  async submitTakeawayOrder(order: TakeawayOrder): Promise<{ success: boolean; orderId?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/takeaway-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(order),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit order');
      }

      const result = await response.json();
      return { success: true, orderId: result.orderId };
    } catch (error) {
      console.error('Error submitting takeaway order:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get menu items from backend
  async getMenuItems(locationId: string): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/locations/${locationId}/menu`);
      if (!response.ok) {
        throw new Error('Failed to fetch menu items');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching menu items:', error);
      // Return sample data as fallback
      return this.getSampleMenuItems();
    }
  }

  // Fallback sample data
  private getSampleLocations(): Location[] {
    return [
      {
        id: '1',
        name: 'P&P Antwerpen Eilandje',
        address: 'Sint-Aldegondiskaai 44',
        city: 'Antwerpen',
        postcode: '2000',
        phone: '+32 3 123 45 67',
        coordinates: [51.2194, 4.4025],
        isOpen: true,
        closingTime: '23:00',
        services: ['McDrive', 'Parking', 'Terras', 'Gratis Wifi'],
        type: 'restaurant'
      },
      {
        id: '2',
        name: 'P&P Gent',
        address: 'Korenmarkt 15',
        city: 'Gent',
        postcode: '9000',
        phone: '+32 9 123 45 67',
        coordinates: [51.0543, 3.7174],
        isOpen: true,
        closingTime: '22:30',
        services: ['Parking', 'Terras', 'Gratis Wifi', 'Tafelbediening'],
        type: 'restaurant'
      },
      {
        id: '3',
        name: 'P&P Brussel',
        address: 'Grote Markt 1',
        city: 'Brussel',
        postcode: '1000',
        phone: '+32 2 123 45 67',
        coordinates: [50.8503, 4.3517],
        isOpen: true,
        closingTime: '23:30',
        services: ['McDelivery', 'Terras', 'Gratis Wifi', 'Tafelbediening'],
        type: 'restaurant'
      },
      {
        id: '4',
        name: 'P&P Leuven',
        address: 'Oude Markt 20',
        city: 'Leuven',
        postcode: '3000',
        phone: '+32 16 123 45 67',
        coordinates: [50.8798, 4.7005],
        isOpen: true,
        closingTime: '22:00',
        services: ['Parking', 'Terras', 'Gratis Wifi'],
        type: 'restaurant'
      },
      {
        id: '5',
        name: 'P&P Brugge',
        address: 'Markt 10',
        city: 'Brugge',
        postcode: '8000',
        phone: '+32 50 123 45 67',
        coordinates: [51.2093, 3.2247],
        isOpen: true,
        closingTime: '22:00',
        services: ['Terras', 'Gratis Wifi', 'Tafelbediening'],
        type: 'restaurant'
      },
      {
        id: '6',
        name: 'P&P Mechelen',
        address: 'Grote Markt 25',
        city: 'Mechelen',
        postcode: '2800',
        phone: '+32 15 123 45 67',
        coordinates: [51.0259, 4.4776],
        isOpen: true,
        closingTime: '22:30',
        services: ['McDrive', 'Parking', 'Gratis Wifi'],
        type: 'drive'
      },
      {
        id: '7',
        name: 'P&P Oostende',
        address: 'Visserskaai 5',
        city: 'Oostende',
        postcode: '8400',
        phone: '+32 59 123 45 67',
        coordinates: [51.2167, 2.9167],
        isOpen: true,
        closingTime: '23:00',
        services: ['Terras', 'Gratis Wifi', 'Tafelbediening'],
        type: 'restaurant'
      },
      {
        id: '8',
        name: 'P&P Etterbeek',
        address: 'Avenue de Tervuren 100',
        city: 'Etterbeek',
        postcode: '1040',
        phone: '+32 2 123 45 68',
        coordinates: [50.8333, 4.3833],
        isOpen: true,
        closingTime: '22:00',
        services: ['McDrive', 'Parking', 'Gratis Wifi'],
        type: 'drive'
      }
    ];
  }

  private getSampleMenuItems(): any[] {
    return [
      {
        id: '1',
        name: 'Poulet Roti',
        description: 'Geroosterde kip met kruiden en groenten',
        price: 18.50,
        category: 'Hoofdgerechten',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Poulet+Roti',
        available: true
      },
      {
        id: '2',
        name: 'Poulet Frites',
        description: 'Kip met frietjes en mayonaise',
        price: 15.90,
        category: 'Hoofdgerechten',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Poulet+Frites',
        available: true
      },
      {
        id: '3',
        name: 'Poulet Vol-au-vent',
        description: 'Kip in vol-au-vent met champignons',
        price: 16.50,
        category: 'Hoofdgerechten',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Vol-au-vent',
        available: true
      },
      {
        id: '4',
        name: 'Frietjes',
        description: 'Verse frietjes met mayonaise',
        price: 4.50,
        category: 'Bijgerechten',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Frietjes',
        available: true
      },
      {
        id: '5',
        name: 'Salade',
        description: 'Verse groene salade met dressing',
        price: 6.50,
        category: 'Bijgerechten',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Salade',
        available: true
      },
      {
        id: '6',
        name: 'Tiramisu',
        description: 'Klassieke tiramisu met koffie',
        price: 7.50,
        category: 'Desserts',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Tiramisu',
        available: true
      },
      {
        id: '7',
        name: 'Cola',
        description: 'Frisdrank 33cl',
        price: 3.50,
        category: 'Dranken',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Cola',
        available: true
      },
      {
        id: '8',
        name: 'Bier',
        description: 'Belgisch bier 33cl',
        price: 4.50,
        category: 'Dranken',
        image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Bier',
        available: true
      }
    ];
  }
}

export const apiService = new ApiService();
